/**
 * Resolve related_content.curated in editor order (pages, products, SPA routes, ABC assets).
 * Shared by PageViewer and TunePage.
 */
export async function resolveCuratedItemsForViewer(curatedList, excludedIds) {
  if (!Array.isArray(curatedList) || !curatedList.length) return [];

  const idsNorm = (x) => (x == null ? '' : String(x));
  const excluded = excludedIds instanceof Set ? excludedIds : new Set();

  const productEntries = curatedList.filter((i) => i.type === 'product');
  const pageEntries = curatedList.filter(
    (i) => i.type !== 'product' && i.type !== 'route' && i.type !== 'tune-asset'
  );

  const productIds = productEntries.map((i) => i.id).filter((id) => id != null && !excluded.has(idsNorm(id)));
  const pageIds = pageEntries.map((i) => i.id).filter((id) => id && !excluded.has(idsNorm(id)));

  const [prodResults, pagesPayload] = await Promise.all([
    Promise.all(
      productIds.map((id) =>
        fetch(`/api/products/${id}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
      )
    ),
    pageIds.length
      ? fetch(`/api/content/pages-by-ids?ids=${encodeURIComponent(pageIds.join(','))}`)
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => [])
      : Promise.resolve([]),
  ]);

  const prodById = new Map(prodResults.filter(Boolean).map((p) => [String(p.id), p]));
  const pageList = Array.isArray(pagesPayload) ? pagesPayload : [];
  const pageById = new Map(pageList.filter(Boolean).map((p) => [String(p.pageId || p.page_id), p]));

  const out = [];
  for (const ci of curatedList) {
    if (ci.type === 'route' && ci.href) {
      out.push({
        itemType: 'route',
        routeHref: ci.href,
        name: ci.title || ci.href,
        title: ci.title || ci.href,
        description: '',
        match_score: 1000,
        fromExplicit: true,
        fromCurated: true,
      });
      continue;
    }
    if (ci.type === 'product') {
      if (excluded.has(idsNorm(ci.id))) continue;
      const p = prodById.get(String(ci.id));
      if (!p) continue;
      out.push({
        ...p,
        itemType: 'product',
        name: p.name,
        match_score: 1000,
        fromExplicit: true,
        fromCurated: true,
      });
      continue;
    }
    if (ci.type === 'tune-asset' && ci.id != null) {
      if (excluded.has(idsNorm(ci.id))) continue;
      out.push({
        itemType: 'tune-asset',
        tuneAssetId: ci.id,
        id: ci.id,
        name: ci.title || `ABC tune #${ci.id}`,
        title: ci.title || `ABC tune #${ci.id}`,
        description: '',
        match_score: 1000,
        fromExplicit: true,
        fromCurated: true,
      });
      continue;
    }
    const id = ci.id;
    if (!id || excluded.has(idsNorm(id))) continue;
    const p = pageById.get(String(id));
    if (!p) continue;
    const pid = p.pageId || p.page_id;
    const pt = p.pageType || p.page_type;
    out.push({
      ...p,
      page_id: pid,
      itemType: pt === 'tune-notation' || pt === 'abc-notation' ? 'tune' : 'article',
      name: p.title,
      match_score: 1000,
      fromExplicit: true,
      fromCurated: true,
    });
  }
  return out;
}
